import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getProducts } from '../services/api'

const Products = () => {
  const [products, setProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [loading, setLoading] = useState(true)

  const sortOptions = [
    { id: 'newest', name: '最新发布' },
    { id: 'name', name: '产品名称' }
  ]

  // 加载产品数据
  useEffect(() => {
    loadProducts()
  }, [])

  // 过滤和排序产品
  useEffect(() => {
    filterProducts()
  }, [searchTerm, sortBy, products])

  const loadProducts = async () => {
    try {
      setLoading(true)
      const result = await getProducts({
        status: 'published',
        limit: 100 // 获取所有已发布的产品
      })
      
      if (result && result.products) {
        setProducts(result.products)
        setFilteredProducts(result.products)
      }
    } catch (error) {
      console.error('加载产品数据失败:', error)
      // 如果API调用失败，显示空状态
      setProducts([])
      setFilteredProducts([])
    } finally {
      setLoading(false)
    }
  }

  const filterProducts = () => {
    let filtered = products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase())
      return matchesSearch
    })

    // 排序
    filtered.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.publishedAt || b.createdAt) - new Date(a.publishedAt || a.createdAt)
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name)
      }
      return 0
    })

    setFilteredProducts(filtered)
  }


  // 处理features字段，转换为数组（去除HTML标签）
  const getFeaturesArray = (features) => {
    if (!features) return []
    
    // 如果已经是数组，直接返回（但需要去除可能的HTML标签）
    if (Array.isArray(features)) {
      return features.map(feature => {
        if (typeof feature === 'string') {
          // 去除HTML标签
          return feature.replace(/<[^>]*>/g, '').trim()
        }
        return feature
      })
    }
    
    // 处理JSON格式的HTML内容
    try {
      let content = ''
      
      if (typeof features === 'string') {
        // 首先检查是否看起来像JSON（以{开头，以}结尾）
        const trimmed = features.trim()
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          try {
            // 尝试解析JSON
            const parsed = JSON.parse(features)
            if (parsed && typeof parsed === 'object') {
              // 提取content字段中的HTML内容
              content = parsed.content || ''
            } else {
              content = features
            }
          } catch (jsonError) {
            // JSON解析失败，可能是由于未转义的引号
            // 尝试修复JSON字符串：转义HTML属性中的双引号
            let fixedJson = features
            // 匹配HTML属性中的双引号并转义它们
            // 但要注意不要转义JSON字符串本身的双引号
            // 简单方法：尝试提取content字段的值部分
            const contentMatch = features.match(/"content"\s*:\s*"([\s\S]*?)"(?=\s*[},])/)
            if (contentMatch && contentMatch[1]) {
              // 直接使用匹配到的内容（不尝试修复引号）
              content = contentMatch[1]
            } else {
              // 如果无法提取，直接当作HTML处理
              content = features
            }
          }
        } else {
          // 不是JSON格式，直接当作HTML处理
          content = features
        }
      } else if (typeof features === 'object') {
        // 直接提取content字段中的HTML内容
        content = features.content || ''
      }
      
      // 如果content是HTML内容，去除标签并分割为数组
      if (content && typeof content === 'string') {
        // 去除HTML标签
        const plainText = content.replace(/<[^>]*>/g, '\n')
          .replace(/\n+/g, '\n')
          .trim()
        
        // 按换行符分割，过滤空行
        const lines = plainText.split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)
        
        // 如果分割后有内容，返回
        if (lines.length > 0) {
          return lines
        }
        
        // 如果没有换行符，尝试按列表项分割
        if (content.includes('<li>') || content.includes('<ol>') || content.includes('<ul>')) {
          // 提取列表项内容
          const listItems = content.match(/<li[^>]*>([^<]*)<\/li>/gi)
          if (listItems && listItems.length > 0) {
            return listItems.map(item => {
              // 去除<li>标签和内部可能的其他标签
              return item.replace(/<[^>]*>/g, '').trim()
            }).filter(item => item.length > 0)
          }
        }
        
        // 如果还是空，返回原始内容（去除HTML标签）
        return [plainText]
      }
      
      // 如果是对象但不是content字段，提取所有字符串值
      if (typeof features === 'object') {
        const values = Object.values(features).filter(val => typeof val === 'string')
        if (values.length > 0) {
          return values.map(val => val.replace(/<[^>]*>/g, '').trim())
        }
      }
      
    } catch (e) {
      console.error('解析features失败:', e)
      // 如果解析失败，尝试直接去除HTML标签
      if (typeof features === 'string') {
        const plainText = features.replace(/<[^>]*>/g, '').trim()
        if (plainText) {
          return [plainText]
        }
      }
    }
    
    return []
  }

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <div className="container-max section-padding">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-tech mb-4">
            产品介绍
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            探索我们的创新产品系列，涵盖硬件、软件及定制化解决方案
          </p>
        </div>


        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600">正在加载产品数据...</p>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map(product => {
              const featuresArray = getFeaturesArray(product.features)
              return (
                <div key={product.id} className="card hover:shadow-lg transition-shadow duration-300">
                  <div className="relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-tech mb-2">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    
                    {featuresArray.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">主要特性：</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {featuresArray.slice(0, 3).map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <svg className="w-4 h-4 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {feature}
                            </li>
                          ))}
                          {featuresArray.length > 3 && (
                            <li className="text-xs text-gray-500">
                              还有 {featuresArray.length - 3} 个特性...
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-primary">
                        {product.priceInfo || '面议'}
                      </span>
                      <Link
                        to={`/products/${product.id}`}
                        className="btn-primary"
                      >
                        查看详情
                      </Link>
                    </div>
                    
                    {/* 访问计数显示 */}
                    <div className="mt-3 flex items-center justify-center text-gray-900" style={{ fontSize: '16px' }}>
                      <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>{product.viewCount || 0} 次访问</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-tech mb-2">未找到相关产品</h3>
            <p className="text-gray-600 mb-4">
              {products.length === 0 
                ? '当前没有可用的产品数据，请稍后再试或联系管理员添加产品。'
                : '请尝试调整搜索条件或筛选条件'}
            </p>
            {products.length === 0 && (
              <button
                onClick={loadProducts}
                className="btn-primary"
              >
                重新加载
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Products
